import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../utils/auth";
import "./Calls.css";
import CallSummary from "../../components/CallSummary/CallSummary";
import CallScores from "../../components/CallScores/CallScores";
import RedFlags from "../../components/RedFlags/RedFlags";
import GreenLights from "../../components/GreenLights/GreenLights";
import ClientDetailsCalls from "../../components/ClientDetailsCalls/ClientDetailsCalls";
import Auth from "../../utils/auth";

const Calls = () => {
  const { clientId, callScoreId } = useParams();
  const [transcript, setTranscript] = useState("");
  const [transcriptId, setTranscriptId] = useState(null);
  const [callScore, setCallScores] = useState(null);
  const [overallScore, setOverallScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redFlags, setRedFlags] = useState([]);
  const [greenFlags, setGreenFlags] = useState([]);
  const [callSummary, setCallSummary] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isSavedSuccessfully, setIsSavedSuccessfully] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [progress, setProgress] = useState(0);

  const workspaceId = Auth.getWorkspaceId();

  useEffect(() => {
    if (!workspaceId) {
      console.error("Workspace ID is missing");
      return;
    }

    if (callScoreId) {
      fetchCallScore();
    }
  }, [callScoreId, workspaceId]);

  const fetchCallScore = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/call-score/get-call-score?id=${callScoreId}&workspaceId=${workspaceId}`
      );
      if (response.status === 200) {
        const { call_score, call_scores, red_flags, green_lights, call_summary, created_at } = response.data;

        setOverallScore(call_score);
        setCallScores(call_scores);
        setRedFlags(red_flags);
        setGreenFlags(green_lights);
        setCallSummary(call_summary);
        setCreatedAt(created_at);
      }
    } catch (error) {
      console.error("Failed to fetch call score:", error);
    }
  };

  const sendTranscript = async () => {
    if (!transcript) {
      setErrorMessage("Transcript is missing. Please upload a valid transcript file.");
      return;
    }

    if (!workspaceId) {
      setErrorMessage("Workspace ID is missing");
      return;
    }

    setLoading(true);
    setCallScores(null);

    try {
      const response = await axios.post(`${API_URL}/call-score/create-score`, {
        transcript,
        workspaceId
      });

      if (response.status === 202) {
        pollForResults(response.data.threadId);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to send transcript:", error);
      setErrorMessage("Failed to send transcript. Please try again.");
      setLoading(false);
    }
  };

  const pollForResults = async (threadId) => {
    let attempts = 0;
    const softLimit = 30;
    const increment = 90 / softLimit;

    while (true) {
      await new Promise((resolve) => setTimeout(resolve, 6000));

      try {
        const resultResponse = await axios.get(
          `${API_URL}/call-score/get-score?threadId=${threadId}&workspaceId=${workspaceId}`
        );

        if (resultResponse.status === 200) {
          const parsedData = resultResponse.data.parsedResult;
          const { overall_score_out_of_10, call_scores, red_flags, green_flags, short_call_summary } = parsedData;

          setOverallScore(overall_score_out_of_10);
          setCallScores(call_scores);
          setRedFlags(red_flags);
          setGreenFlags(green_flags);
          setCallSummary(short_call_summary);

          setProgress(100);
          setLoading(false);
          return;
        } else if (resultResponse.status === 202) {
          if (attempts < softLimit) {
            setProgress((prevProgress) => Math.min(prevProgress + increment, 90));
          }
        } else {
        setErrorMessage("Unexpected response from server.");
        setLoading(false);
        return;
      }
      } catch (error) {
        console.error("Polling error:", error);
         setErrorMessage("Error while checking the status. Please try again later.");
      setLoading(false);
      return;
      }
      attempts++;
    }
  };

  const saveCallScore = async () => {
    if (!callScore) {
      setErrorMessage("No call score to save.");
      return;
    }

    if (!workspaceId) {
      setErrorMessage("Workspace ID is missing");
      return;
    }

    setSaving(true);
    setErrorMessage(null);

    try {
      const response = await axios.post(`${API_URL}/call-score/store-call-score`, {
        transcriptId,
        clientId,
        callScores: callScore,
        redFlags: redFlags,
        greenLights: greenFlags,
        callScore: overallScore,
        callSummary,
        workspaceId
      });

      if (response.status === 201) {
        setIsSavedSuccessfully(true);
        setCreatedAt(new Date().toISOString());
      } else {
        setErrorMessage("Error saving call score.");
      }
    } catch (error) {
      console.error("Failed to save call score:", error);
      setErrorMessage("Failed to save call score.");
    }

    setSaving(false);
  };

  return (
    <div className="calls-container">
      <div className="column-left">
        <ClientDetailsCalls
          setTranscript={setTranscript}
          score={overallScore}
          sendTranscript={sendTranscript}
          loading={loading}
          saveCallScore={saveCallScore}
          callScoreAvailable={callScore !== null}
          setTranscriptId={setTranscriptId}
          isSavedSuccessfully={isSavedSuccessfully}
          errorMessage={errorMessage}
          progress={progress}
          callScores={callScore}
          redFlags={redFlags}
          greenFlags={greenFlags}
          callSummary={callSummary}
          createdAt={createdAt}
        />
      </div>
      <div className="column-center">
        <CallSummary transcript={transcript} callSummary={callSummary} />
        <RedFlags redFlags={redFlags} />
      </div>
      <div className="column-right">
        <CallScores scores={callScore} />
        <GreenLights greenFlags={greenFlags} />
      </div>
    </div>
  );
};

export default Calls;